import { useState, useMemo, useCallback } from "react";
import { Building2, Search, MapPin, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import { useAccountContext } from "../context/AccountContext";
import { useQuickFacts } from "../hooks/useQuickFacts";
import { useAccountExport } from "../hooks/useApi";
import { QuickFactsCard } from "../components/QuickFactsCard";
import { AccountBulkBar } from "../components/AccountBulkBar";
import { ExportModal } from "../components/ExportModal";
import type { AccountExportItem } from "../types";

export function AccountPage() {
  const { accounts, loading } = useAccountContext();
  const [query, setQuery] = useState("");
  const [expandedUuid, setExpandedUuid] = useState<string | null>(null);

  // Multi-select state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const selectionMode = selected.size > 0;

  // Export state
  const { exportAccounts, loading: exporting } = useAccountExport();
  const [exportItems, setExportItems] = useState<AccountExportItem[] | null>(
    null
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return accounts;
    const q = query.trim().toLowerCase();
    return accounts.filter(
      (a) =>
        a.display_name.toLowerCase().includes(q) ||
        (a.city && a.city.toLowerCase().includes(q)) ||
        (a.state && a.state.toLowerCase().includes(q))
    );
  }, [accounts, query]);

  const toggleSelect = useCallback((uuid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }, []);

  const selectAllFiltered = useCallback(() => {
    setSelected(new Set(filtered.map((a) => a.uuid)));
  }, [filtered]);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
  }, []);

  const handleExportEmails = useCallback(async () => {
    const items = await exportAccounts({
      account_uuids: Array.from(selected),
      select_all: false,
    });
    setExportItems(items);
  }, [selected, exportAccounts]);

  const handleRowClick = useCallback(
    (uuid: string) => {
      if (selectionMode) {
        toggleSelect(uuid);
      } else {
        setExpandedUuid((prev) => (prev === uuid ? null : uuid));
      }
    },
    [selectionMode, toggleSelect]
  );

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent, uuid: string) => {
      e.stopPropagation();
      toggleSelect(uuid);
    },
    [toggleSelect]
  );

  return (
    <div className="flex flex-1 flex-col px-4 py-4">
      {/* Search bar */}
      <div
        className={cn(
          "flex items-center gap-2 rounded-xl border px-3",
          "bg-surface-elevated border-border"
        )}
      >
        <Search className="h-4 w-4 shrink-0 text-text-muted" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search accounts..."
          className={cn(
            "h-touch flex-1 bg-transparent text-sm text-text-primary",
            "placeholder:text-text-muted focus:outline-none"
          )}
        />
        {query && (
          <span className="text-xs text-text-muted">{filtered.length}</span>
        )}
      </div>

      {/* Bulk action bar */}
      {selectionMode && (
        <div className="mt-2">
          <AccountBulkBar
            count={selected.size}
            totalCount={filtered.length}
            onSelectAll={selectAllFiltered}
            onExportEmails={handleExportEmails}
            onClear={clearSelection}
            exporting={exporting}
          />
        </div>
      )}

      {/* Account count + Select All toggle */}
      {!loading && (
        <div className="mt-3 flex items-center justify-between">
          <p className="text-xs text-text-muted">{accounts.length} accounts</p>
          {!selectionMode && filtered.length > 0 && (
            <button
              type="button"
              onClick={selectAllFiltered}
              className="text-xs text-brand hover:text-brand/80"
              data-testid="select-all-toggle"
            >
              Select All
            </button>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center py-8">
          <Building2 className="h-8 w-8 text-text-muted" />
          <p className="mt-2 text-sm text-text-muted">
            {query.trim()
              ? "No accounts match your search."
              : "No accounts assigned."}
          </p>
        </div>
      ) : (
        <div className="mt-2 space-y-1 overflow-y-auto">
          {filtered.map((account) => (
            <div
              key={account.uuid}
              data-testid={`account-row-${account.uuid}`}
            >
              <button
                type="button"
                data-testid={`account-button-${account.uuid}`}
                onClick={() => handleRowClick(account.uuid)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-3 py-3",
                  "text-left transition-colors active:bg-surface-inset",
                  expandedUuid === account.uuid && "bg-surface-inset",
                  selected.has(account.uuid) && "bg-brand/5 border border-brand/20"
                )}
              >
                {/* Checkbox */}
                <div
                  role="checkbox"
                  aria-checked={selected.has(account.uuid)}
                  tabIndex={0}
                  onClick={(e) => handleCheckboxClick(e, account.uuid)}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSelect(account.uuid);
                    }
                  }}
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
                    selected.has(account.uuid)
                      ? "bg-brand border-brand text-white"
                      : "border-border hover:border-brand/50"
                  )}
                  data-testid={`account-checkbox-${account.uuid}`}
                >
                  {selected.has(account.uuid) && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>

                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <Building2 className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {account.display_name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    {(account.city || account.state) && (
                      <span className="flex items-center gap-0.5">
                        <MapPin className="h-3 w-3" />
                        {[account.city, account.state]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}
                    {account.industry_type && (
                      <span className="capitalize">
                        {account.industry_type}
                      </span>
                    )}
                  </div>
                </div>
              </button>

              {/* Inline Quick Facts -- expands below the account row */}
              {expandedUuid === account.uuid && !selectionMode && (
                <InlineQuickFacts accountUuid={account.uuid} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Export Modal */}
      {exportItems !== null && (
        <ExportModal
          items={exportItems}
          onClose={() => setExportItems(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline Quick Facts for expanded account row
// ---------------------------------------------------------------------------

function InlineQuickFacts({ accountUuid }: { accountUuid: string }) {
  const { data, loading, error } = useQuickFacts(accountUuid);

  return (
    <div className="px-3 pb-3">
      <QuickFactsCard
        data={data}
        loading={loading}
        error={error}
        className="mt-1"
      />
    </div>
  );
}
